import _, {isEqual, mapValues, union} from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {createResourcesUpdatedReducer} from '../../../helpers/models/resourceHelpers';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {AppAction} from '../../appActions';
import {ResourceMap} from '../../appTypes';
import {DataGroupsActionsEnum} from '../dataGroupsActions';
import {LocalApplicationActionsEnum} from './localApplicationsActions';

const localApplicationsUpdatedReducer = createResourcesUpdatedReducer(
  'localApplications',
  (localApplication) => localApplication.id,
  mergeLocalApplications,
);

export function localApplicationsReducer(
  state: ResourceMap<LocalApplicationModel>,
  action: AppAction,
): ResourceMap<LocalApplicationModel> {
  switch (action.type) {
    case DataGroupsActionsEnum.DATA_UPDATED:
      return localApplicationsUpdatedReducer(state, action);

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_DELETED: {
      return _.omit(state, action.payload.localApplicationId);
    }

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          servers: {
            status: AsyncStatusesEnum.LOADING,
            items: localApplication.servers?.items ?? [],
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_COMPLETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          servers: {
            status: AsyncStatusesEnum.LOADED,
            items: action.payload.servers,
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_SERVERS_FAILED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          servers: {
            status: AsyncStatusesEnum.FAILED,
            items: [],
          },
        };
      });

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_CREATED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          servers: {
            status: AsyncStatusesEnum.LOADED,
            items: union(localApplication.servers?.items, [action.payload.server]),
          },
        };
      });

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_SERVER_UPDATED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          servers: {
            status: AsyncStatusesEnum.LOADED,
            items: _(localApplication.servers?.items ?? [])
              .map((server) => {
                if (server.id === action.payload.server.id) {
                  return action.payload.server;
                }
                return server;
              })
              .value(),
          },
        };
      });

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_CREATED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          channelType: {
            id: action.payload.channelType.id,
            status: AsyncStatusesEnum.LOADED,
            value: action.payload.channelType,
          },
        };
      });
    case LocalApplicationActionsEnum.LOCAL_APPLICATION_CHANNEL_TYPE_DELETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          channelType: undefined,
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId || !localApplication.channelType) {
          return localApplication;
        }
        return {
          ...localApplication,
          channelType: {
            ...localApplication.channelType,
            status: AsyncStatusesEnum.LOADING,
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_COMPLETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId || !localApplication.channelType) {
          return localApplication;
        }
        return {
          ...localApplication,
          channelType: {
            ...localApplication.channelType,
            status: AsyncStatusesEnum.LOADED,
            value: action.payload.channelType,
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_CHANNEL_TYPE_FAILED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId || !localApplication.channelType) {
          return localApplication;
        }
        return {
          ...localApplication,
          channelType: {
            ...localApplication.channelType,
            status: AsyncStatusesEnum.FAILED,
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          entryPoints: {
            status: AsyncStatusesEnum.LOADING,
            items: [],
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_COMPLETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          entryPoints: {
            status: AsyncStatusesEnum.LOADED,
            items: action.payload.entryPoints,
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_ENTRY_POINTS_FAILED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          entryPoints: {
            status: AsyncStatusesEnum.FAILED,
            items: [],
          },
        };
      });

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_CREATED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          entryPoints: {
            status: AsyncStatusesEnum.LOADED,
            items: union(localApplication?.entryPoints?.items, [action.payload.entryPoint]),
          },
        };
      });

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_ENTRY_POINT_DELETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          entryPoints: {
            status: AsyncStatusesEnum.LOADED,
            items: (localApplication.entryPoints?.items || []).filter(
              (item) => item.id !== action.payload.entryPointId,
            ),
          },
        };
      });

    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          oAuthClient: {
            status: AsyncStatusesEnum.LOADING,
            value: undefined,
          },
        };
      });
    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_COMPLETED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          oAuthClient: {
            status: AsyncStatusesEnum.LOADED,
            value: action.payload.oAuthClient,
          },
        };
      });
    case LocalApplicationActionsEnum.FETCH_LOCAL_APPLICATION_OAUTH_CLIENT_FAILED:
      return mapValues(state, (localApplication) => {
        if (localApplication?.id !== action.payload.localApplicationId) {
          return localApplication;
        }
        return {
          ...localApplication,
          oAuthClient: {
            status: AsyncStatusesEnum.FAILED,
            value: undefined,
          },
        };
      });

    default:
      return state;
  }
}

function mergeLocalApplications(
  existingLocalApplication: LocalApplicationModel | undefined,
  updatedLocalApplication: LocalApplicationModel,
) {
  if (!existingLocalApplication) {
    return updatedLocalApplication;
  }

  if (isEqual(existingLocalApplication, updatedLocalApplication)) {
    return existingLocalApplication;
  }

  return updatedLocalApplication;
}
